import React, { useCallback, useEffect, useState } from "react";
import { apiClient, formatApiError, useAuth } from "@/lib/auth";
import FinanceLayout, { fmtUsd } from "./FinanceLayout";
import {
  Plus, Loader2, Trash2, Building2, Landmark, Wallet, Receipt,
  AlertTriangle, CheckCircle2, DollarSign, Star, StarOff,
  Pencil, Archive, ArchiveRestore, X,
} from "lucide-react";
import { toast } from "sonner";

const todayIso = () => new Date().toISOString().slice(0, 10);

const BillsPage = () => {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [bills, setBills] = useState([]);
  const [summary, setSummary] = useState({ pending_count: 0, pending_total: 0, overdue_count: 0, paid_this_month: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState("pending");

  // Add-bank-account modal state
  const [showAddAcct, setShowAddAcct] = useState(false);
  const [a_nickname, setANickname] = useState("");
  const [a_institution, setAInstitution] = useState("The Citizens Bank");
  const [a_last4, setALast4] = useState("");
  const [a_balance, setABalance] = useState("");

  // Add-bill modal state
  const [showAddBill, setShowAddBill] = useState(false);
  const [b_vendor, setBVendor] = useState("");
  const [b_amount, setBAmount] = useState("");
  const [b_due, setBDue] = useState("");
  const [b_memo, setBMemo] = useState("");

  // Pay modal
  const [payingBill, setPayingBill] = useState(null);
  const [p_account, setPAccount] = useState("");
  const [p_ref, setPRef] = useState("");

  // Deposit modal
  const [depositAcct, setDepositAcct] = useState(null);
  const [d_amount, setDAmount] = useState("");
  const [d_memo, setDMemo] = useState("");

  // Edit bill modal (partner)
  const [editingBill, setEditingBill] = useState(null);
  const [eb_vendor, setEbVendor] = useState("");
  const [eb_amount, setEbAmount] = useState("");
  const [eb_due, setEbDue] = useState("");
  const [eb_memo, setEbMemo] = useState("");
  const [showBillsArchived, setShowBillsArchived] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (showBillsArchived) params.set("include_archived", "true");
      const [a, b] = await Promise.all([
        apiClient.get("/finance/bank-accounts"),
        apiClient.get(`/finance/bills?${params.toString()}`),
      ]);
      setAccounts(a.data.items);
      setBills(b.data.items);
      setSummary(b.data.summary);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [statusFilter, showBillsArchived]);

  useEffect(() => { load(); }, [load]);

  const addAccount = async (e) => {
    e.preventDefault();
    if (!a_nickname) return toast.error("Nickname required");
    setBusy(true);
    try {
      await apiClient.post("/finance/bank-accounts", {
        nickname: a_nickname,
        institution: a_institution,
        last4: a_last4,
        opening_balance: Number(a_balance || 0),
        is_primary: accounts.length === 0,
      });
      toast.success("Bank account added");
      setShowAddAcct(false);
      setANickname(""); setALast4(""); setABalance("");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const setPrimary = async (id) => {
    try {
      await apiClient.patch(`/finance/bank-accounts/${id}`, { is_primary: true });
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteAcct = async (id) => {
    if (!window.confirm("Remove this bank account? Historic transactions stay in the ledger.")) return;
    try {
      await apiClient.delete(`/finance/bank-accounts/${id}`);
      toast.success("Removed");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const addBill = async (e) => {
    e.preventDefault();
    if (!b_vendor || !b_amount || !b_due) return toast.error("Vendor, amount, and due date required");
    setBusy(true);
    try {
      await apiClient.post("/finance/bills", {
        vendor_name: b_vendor,
        amount: Number(b_amount),
        due_date: b_due,
        memo: b_memo,
      });
      toast.success("Bill added");
      setShowAddBill(false);
      setBVendor(""); setBAmount(""); setBDue(""); setBMemo("");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const deleteBill = async (id) => {
    if (!window.confirm("Delete this bill? This removes it from the AP dashboard.")) return;
    try {
      await apiClient.delete(`/finance/bills/${id}`);
      toast.success("Bill deleted");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openPay = (bill) => {
    const primary = accounts.find((a) => a.is_primary) || accounts[0];
    if (!primary) return toast.error("Add a bank account first");
    setPayingBill(bill);
    setPAccount(primary.id);
    setPRef("");
  };

  const doPay = async () => {
    if (!payingBill) return;
    setBusy(true);
    try {
      await apiClient.post(`/finance/bills/${payingBill.id}/pay`, {
        bank_account_id: p_account,
        reference: p_ref,
      });
      toast.success(`Paid $${payingBill.amount.toFixed(2)} to ${payingBill.vendor_name}`);
      setPayingBill(null);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const doDeposit = async () => {
    if (!depositAcct || !d_amount) return toast.error("Amount required");
    setBusy(true);
    try {
      await apiClient.post(`/finance/bank-accounts/${depositAcct.id}/deposit`, {
        amount: Number(d_amount),
        memo: d_memo,
      });
      toast.success(`Deposited ${fmtUsd(d_amount)} to ${depositAcct.nickname}`);
      setDepositAcct(null); setDAmount(""); setDMemo("");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const openEditBill = (bill) => {
    setEditingBill(bill);
    setEbVendor(bill.vendor_name);
    setEbAmount(String(bill.amount));
    setEbDue(bill.due_date);
    setEbMemo(bill.memo || "");
  };

  const saveEditBill = async (e) => {
    e.preventDefault();
    if (!editingBill) return;
    setBusy(true);
    try {
      await apiClient.patch(`/finance/bills/${editingBill.id}`, {
        vendor_name: eb_vendor,
        amount: Number(eb_amount),
        due_date: eb_due,
        memo: eb_memo,
      });
      toast.success("Bill updated");
      setEditingBill(null);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const archiveBill = async (bill) => {
    try {
      await apiClient.post(`/finance/bills/${bill.id}/${bill.archived ? "unarchive" : "archive"}`);
      toast.success(bill.archived ? "Restored" : "Archived");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const isOverdue = (bill) => bill.status === "pending" && bill.due_date < todayIso();
  const partnerRequired = (amount) => amount >= 1000 && !user?.is_partner;

  return (
    <FinanceLayout>
      {/* ── HEADER ── */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <div className="mono-label mb-2">// ACCOUNTS PAYABLE</div>
          <h1 className="serif-head text-3xl">Bills &amp; Banking</h1>
          <p className="text-nex-chrome mt-1 text-sm">
            Track every bill, pay from your bank, keep the ledger tight. Bills of $1,000+ require a partner to pay.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setShowAddAcct(true)} className="tick-btn-secondary text-xs !py-2 !px-3" data-testid="add-account-btn">
            <Landmark size={13} /> Add bank account
          </button>
          <button onClick={() => setShowAddBill(true)} className="tick-btn-primary text-xs !py-2 !px-3" data-testid="add-bill-btn">
            <Plus size={13} /> New bill
          </button>
        </div>
      </div>

      {/* ── SUMMARY STRIP ── */}
      <div className="grid md:grid-cols-4 gap-3 mb-6" data-testid="bills-summary">
        <SummaryCard label="Pending bills"    value={summary.pending_count}                     tone="amber"   Icon={Receipt} />
        <SummaryCard label="Pending total"    value={fmtUsd(summary.pending_total)}             tone="silver"  Icon={DollarSign} />
        <SummaryCard label="Overdue"          value={summary.overdue_count}                     tone={summary.overdue_count ? "red" : "silver"} Icon={AlertTriangle} />
        <SummaryCard label="Paid this month"  value={fmtUsd(summary.paid_this_month)}           tone="emerald" Icon={CheckCircle2} />
      </div>

      {/* ── BANK ACCOUNTS ── */}
      <div className="mb-8">
        <div className="mono-label mb-3 flex items-center gap-2">
          <Wallet size={12} /> BANK ACCOUNTS
        </div>
        {accounts.length === 0 ? (
          <div className="card-industrial corner-tick p-6 text-center text-nex-chrome text-sm" data-testid="no-accounts">
            No bank accounts yet. Click <span className="text-nex-gold">Add bank account</span> above or connect Plaid on the Transactions page.
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-3" data-testid="accounts-grid">
            {accounts.map((acct) => (
              <div key={acct.id} className="card-industrial corner-tick p-5 relative" data-testid={`account-${acct.id}`}>
                <div className="flex items-start justify-between mb-3 gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Building2 size={14} className="text-nex-gold shrink-0" />
                      <span className="text-nex-silver font-semibold truncate">{acct.nickname}</span>
                    </div>
                    {acct.institution && (
                      <div className="mono-body text-[10px] text-nex-muted mt-0.5">
                        {acct.institution}{acct.last4 ? ` · ••${acct.last4}` : ""}
                      </div>
                    )}
                  </div>
                  {acct.is_primary && <span className="mono-body text-[9px] uppercase tracking-widest px-2 py-1 bg-nex-gold/10 border border-nex-gold/50 text-nex-gold flex items-center gap-1"><Star size={10} /> Primary</span>}
                </div>
                <div className="serif-head text-2xl text-emerald-300">{fmtUsd(acct.balance)}</div>
                <div className="mono-body text-[10px] uppercase tracking-widest text-nex-muted mt-1">Current balance</div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <button onClick={() => setDepositAcct(acct)} className="tick-btn-secondary text-[10px] !py-1.5 !px-2.5" data-testid={`deposit-${acct.id}`}>
                    <Plus size={11} /> Deposit
                  </button>
                  {!acct.is_primary && (
                    <button onClick={() => setPrimary(acct.id)} className="tick-btn-secondary text-[10px] !py-1.5 !px-2.5" data-testid={`primary-${acct.id}`}>
                      <StarOff size={11} /> Make primary
                    </button>
                  )}
                  {user?.is_partner && (
                    <button onClick={() => deleteAcct(acct.id)} className="tick-btn-secondary text-[10px] !py-1.5 !px-2.5 text-red-300 hover:border-red-400" data-testid={`delete-account-${acct.id}`}>
                      <Trash2 size={11} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── BILLS ── */}
      <div>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="mono-label flex items-center gap-2">
            <Receipt size={12} /> BILLS
          </div>
          <div className="flex items-center gap-3">
            {user?.is_partner && (
              <label className="flex items-center gap-2 text-nex-chrome text-xs cursor-pointer select-none">
                <input type="checkbox" checked={showBillsArchived} onChange={(e) => setShowBillsArchived(e.target.checked)} data-testid="bills-show-archived" className="accent-nex-gold" />
                Show archived
              </label>
            )}
            <div className="flex gap-1">
              {["pending", "paid", ""].map((s) => (
                <button
                  key={s || "all"}
                  onClick={() => setStatusFilter(s)}
                  data-testid={`filter-${s || "all"}`}
                  className={`mono-body text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border transition-colors ${
                    statusFilter === s ? "border-nex-gold text-nex-gold bg-nex-gold/5" : "border-nex-border text-nex-chrome hover:text-nex-silver"
                  }`}
                >
                  {s || "All"}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="card-industrial corner-tick overflow-hidden" data-testid="bills-table">
          {loading ? (
            <div className="text-center py-16"><Loader2 className="animate-spin text-nex-gold mx-auto" /></div>
          ) : bills.length === 0 ? (
            <div className="text-center py-16 text-nex-chrome text-sm">No bills in this view.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-black/30 border-b border-nex-border">
                  <tr className="mono-body text-[10px] uppercase tracking-[0.15em] text-nex-chrome">
                    <th className="text-left px-4 py-3">Vendor</th>
                    <th className="text-left px-4 py-3">Due</th>
                    <th className="text-right px-4 py-3">Amount</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3">Memo</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {bills.map((bill) => (
                    <tr key={bill.id} className={`border-b border-nex-border/50 hover:bg-white/[0.02] ${bill.archived ? "opacity-50" : ""}`} data-testid={`bill-row-${bill.id}`}>
                      <td className="px-4 py-3 text-nex-silver font-medium">
                        <div className="flex items-center gap-2">
                          {bill.vendor_name}
                          {bill.archived && <span className="mono-body text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-nex-border/40 text-nex-muted">Archived</span>}
                        </div>
                      </td>
                      <td className={`px-4 py-3 mono-body text-[11px] whitespace-nowrap ${isOverdue(bill) ? "text-red-300" : "text-nex-chrome"}`}>
                        {bill.due_date}{isOverdue(bill) && " · OVERDUE"}
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-nex-chrome">{fmtUsd(bill.amount)}</td>
                      <td className="px-4 py-3">
                        <StatusPill status={bill.status} />
                      </td>
                      <td className="px-4 py-3 text-nex-muted text-[12px]">{bill.memo}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {bill.status === "pending" && (
                          <button
                            onClick={() => openPay(bill)}
                            disabled={partnerRequired(bill.amount)}
                            title={partnerRequired(bill.amount) ? "Partner-only (bill ≥ $1,000)" : ""}
                            data-testid={`pay-${bill.id}`}
                            className="tick-btn-primary text-[10px] !py-1.5 !px-3 disabled:opacity-40 disabled:cursor-not-allowed mr-2"
                          >
                            Pay
                          </button>
                        )}
                        {bill.status === "paid" && (
                          <span className="mono-body text-[10px] text-emerald-300 mr-2">Paid {bill.paid_at}</span>
                        )}
                        {user?.is_partner && (
                          <>
                            <button onClick={() => openEditBill(bill)} className="text-nex-chrome hover:text-nex-gold mr-3" data-testid={`edit-bill-${bill.id}`} title="Edit"><Pencil size={14} /></button>
                            <button onClick={() => archiveBill(bill)} className="text-nex-chrome hover:text-nex-gold mr-3" data-testid={`archive-bill-${bill.id}`} title={bill.archived ? "Restore" : "Archive"}>
                              {bill.archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                            </button>
                            <button onClick={() => deleteBill(bill.id)} className="text-nex-muted hover:text-red-300" data-testid={`delete-bill-${bill.id}`} title="Delete"><Trash2 size={14} /></button>
                          </>
                        )}
                        {!user?.is_partner && bill.status === "pending" && (
                          <button onClick={() => deleteBill(bill.id)} className="text-nex-muted hover:text-red-300" data-testid={`delete-bill-${bill.id}`}>
                            <Trash2 size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── ADD ACCOUNT MODAL ── */}
      {showAddAcct && (
        <Modal onClose={() => setShowAddAcct(false)} title="Add Bank Account" testid="add-account-modal">
          <form onSubmit={addAccount} className="space-y-3">
            <Field label="Nickname"     value={a_nickname}    onChange={setANickname}   placeholder="e.g. Citizens Operating"    testid="acct-nickname" />
            <Field label="Institution"  value={a_institution} onChange={setAInstitution} placeholder="The Citizens Bank"         testid="acct-institution" />
            <Field label="Account last 4" value={a_last4}     onChange={setALast4}      placeholder="7891"                       testid="acct-last4" maxLength={4} />
            <Field label="Opening balance ($)" value={a_balance} onChange={setABalance} placeholder="0.00" type="number"          testid="acct-balance" />
            <button type="submit" disabled={busy} className="tick-btn-primary w-full justify-center" data-testid="acct-save">
              {busy ? <Loader2 size={13} className="animate-spin" /> : "Save account"}
            </button>
          </form>
        </Modal>
      )}

      {/* ── ADD BILL MODAL ── */}
      {showAddBill && (
        <Modal onClose={() => setShowAddBill(false)} title="New Bill" testid="add-bill-modal">
          <form onSubmit={addBill} className="space-y-3">
            <Field label="Vendor"      value={b_vendor} onChange={setBVendor} placeholder="Office Supplies Inc"     testid="bill-vendor" />
            <Field label="Amount ($)"  value={b_amount} onChange={setBAmount} placeholder="0.00" type="number"       testid="bill-amount" />
            <Field label="Due date"    value={b_due}    onChange={setBDue}    type="date"                            testid="bill-due" />
            <Field label="Memo"        value={b_memo}   onChange={setBMemo}   placeholder="Optional"                 testid="bill-memo" />
            <button type="submit" disabled={busy} className="tick-btn-primary w-full justify-center" data-testid="bill-save">
              {busy ? <Loader2 size={13} className="animate-spin" /> : "Save bill"}
            </button>
          </form>
        </Modal>
      )}

      {/* ── PAY MODAL ── */}
      {payingBill && (
        <Modal onClose={() => setPayingBill(null)} title={`Pay ${payingBill.vendor_name}`} testid="pay-modal">
          <div className="text-center py-2 mb-2">
            <div className="serif-head text-3xl text-nex-gold">{fmtUsd(payingBill.amount)}</div>
            <div className="mono-body text-[10px] uppercase tracking-widest text-nex-muted mt-1">Due {payingBill.due_date}</div>
          </div>
          <div className="space-y-3">
            <label className="block">
              <div className="mono-body text-[10px] uppercase tracking-widest text-nex-chrome mb-1">Pay from</div>
              <select value={p_account} onChange={(e) => setPAccount(e.target.value)} className="input-industrial w-full" data-testid="pay-account">
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>{a.nickname} · {fmtUsd(a.balance)}</option>
                ))}
              </select>
            </label>
            <Field label="Bank reference # (optional)" value={p_ref} onChange={setPRef} placeholder="Chase confirm #123" testid="pay-ref" />
            <button onClick={doPay} disabled={busy} className="tick-btn-primary w-full justify-center" data-testid="pay-confirm">
              {busy ? <Loader2 size={13} className="animate-spin" /> : `Confirm payment of ${fmtUsd(payingBill.amount)}`}
            </button>
            <p className="text-[11px] text-nex-muted text-center leading-relaxed">
              This records the payment in NEX and decrements the account balance. You still need to send the payment via your bank.
            </p>
          </div>
        </Modal>
      )}

      {/* ── DEPOSIT MODAL ── */}
      {depositAcct && (
        <Modal onClose={() => setDepositAcct(null)} title={`Deposit to ${depositAcct.nickname}`} testid="deposit-modal">
          <div className="space-y-3">
            <Field label="Amount ($)" value={d_amount} onChange={setDAmount} placeholder="0.00" type="number" testid="deposit-amount" />
            <Field label="Memo"       value={d_memo}   onChange={setDMemo}   placeholder="Optional"           testid="deposit-memo" />
            <button onClick={doDeposit} disabled={busy} className="tick-btn-primary w-full justify-center" data-testid="deposit-confirm">
              {busy ? <Loader2 size={13} className="animate-spin" /> : "Log deposit"}
            </button>
          </div>
        </Modal>
      )}

      {/* ── EDIT BILL MODAL (partner) ── */}
      {editingBill && (
        <Modal onClose={() => setEditingBill(null)} title={`Edit Bill · ${editingBill.vendor_name}`} testid="edit-bill-modal">
          <form onSubmit={saveEditBill} className="space-y-3">
            <Field label="Vendor"     value={eb_vendor} onChange={setEbVendor} placeholder="Vendor name" testid="ebill-vendor" />
            <Field label="Amount ($)" value={eb_amount} onChange={setEbAmount} type="number"             testid="ebill-amount" />
            <Field label="Due date"   value={eb_due}    onChange={setEbDue}    type="date"               testid="ebill-due" />
            <Field label="Memo"       value={eb_memo}   onChange={setEbMemo}   placeholder="Optional"    testid="ebill-memo" />
            <button type="submit" disabled={busy} className="tick-btn-primary w-full justify-center" data-testid="ebill-save">
              {busy ? <Loader2 size={13} className="animate-spin" /> : "Save changes"}
            </button>
            {editingBill.status === "paid" && (
              <p className="text-[11px] text-amber-300/80 text-center leading-relaxed">
                Editing a paid bill only updates NEX bookkeeping — it does not reverse the ACH sent from your bank.
              </p>
            )}
          </form>
        </Modal>
      )}
    </FinanceLayout>
  );
};

/* ── Small internal presentation helpers ── */
const SummaryCard = ({ label, value, tone, Icon }) => {
  const toneMap = {
    amber:   "border-amber-500/30 text-amber-300",
    silver:  "border-nex-border text-nex-silver",
    red:     "border-red-500/40 text-red-300",
    emerald: "border-emerald-500/30 text-emerald-300",
  };
  return (
    <div className={`card-industrial corner-tick p-4 border ${toneMap[tone]}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="mono-body text-[10px] uppercase tracking-widest text-nex-muted">{label}</span>
        <Icon size={14} />
      </div>
      <div className="serif-head text-2xl">{value}</div>
    </div>
  );
};

const StatusPill = ({ status }) => {
  const map = {
    pending:   "bg-amber-500/10 border-amber-500/40 text-amber-300",
    scheduled: "bg-blue-500/10 border-blue-500/40 text-blue-300",
    paid:      "bg-emerald-500/10 border-emerald-500/40 text-emerald-300",
    cancelled: "bg-nex-border text-nex-muted",
  };
  return (
    <span className={`mono-body text-[9px] uppercase tracking-widest px-2 py-1 border ${map[status] || map.pending}`}>
      {status}
    </span>
  );
};

const Modal = ({ children, onClose, title, testid }) => (
  <>
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[75]" onClick={onClose} />
    <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[80] w-full max-w-md px-4" data-testid={testid}>
      <div className="card-industrial corner-tick p-6">
        <h3 className="serif-head text-xl mb-4">{title}</h3>
        {children}
      </div>
    </div>
  </>
);

const Field = ({ label, value, onChange, placeholder, type = "text", testid, maxLength }) => (
  <label className="block">
    <div className="mono-body text-[10px] uppercase tracking-widest text-nex-chrome mb-1">{label}</div>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLength}
      className="input-industrial w-full"
      data-testid={testid}
    />
  </label>
);

export default BillsPage;
